import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Globe, Package, Users, Star, ExternalLink, Pencil, Link2, CalendarClock, RefreshCw } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { EnumBadge } from '@/components/ui/enum-badge';
import { useTranslation } from 'react-i18next';
import { usePackage, usePartners, usePartnershipsAccess, canEditSection } from '@/hooks/usePartnerships';
import { PARTNER_CONFIG } from '@/components/partnerships/PartnerTheme';
import { PackageFormDialog } from '@/components/partnerships/PackageFormDialog';
import { tierBadgeClass, variantLabel } from '@/components/partnerships/PackageCard';
import { enumLabel, fieldHelp, fieldLabel, sectionTitle } from '@/components/partnerships/package-sheet-ui';
import { InternalBadge } from '@/components/partnerships/InternalBadge';
import type { Package as PartnerPackage, PackageVariant, PartnerSlug } from '../../../../shared/types/partnerships';
import { effectiveVariants, isPackageExpired } from '../../../../shared/partnerships-packages';
import {
  PACKAGE_SHEET, getPath, sectionFilled, sheetCompleteness,
  type PackageSheetField, type PackageSheetSection,
} from '../../../../shared/partnerships-package-sheet';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { formatDate } from '@/lib/utils';
import { fmtRelative } from '@/lib/relative-time';

type T = ReturnType<typeof useTranslation>['t'];

/** Un campo simple de la ficha, según su tipo (distinto del FieldValue de OML: acá no hay candado ni permisos). */
function SheetFieldValue({ t, field, value }: { t: T; field: PackageSheetField; value: unknown }) {
  const s = String(value);
  if (field.type === 'url') {
    return (
      <a href={s} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline inline-flex items-center gap-1 break-all">
        <ExternalLink className="h-3.5 w-3.5 shrink-0" /> {s}
      </a>
    );
  }
  if (field.type === 'select') return <EnumBadge value={s} tone="info" label={enumLabel(t, s)} />;
  if (field.type === 'date') return <p className="text-sm font-medium">{formatDate(s)}</p>;
  return <p className={`text-sm text-muted-foreground whitespace-pre-line ${field.type === 'textarea' ? '' : 'font-medium text-foreground'}`}>{s}</p>;
}

/** Sección de campos simples (lo que el registro describe). Las custom tienen su bloque abajo. */
function SheetSection({ t, pkg, section }: { t: T; pkg: PartnerPackage; section: PackageSheetSection }) {
  const fields = section.fields.filter((f) => {
    const v = getPath(pkg, f.path);
    return v != null && String(v).trim() !== '';
  });
  if (!fields.length) return null;
  return (
    <Card className={`p-5 space-y-3 ${section.internal ? 'border-dashed border-amber-300/70 bg-amber-50/40 dark:bg-amber-950/10 dark:border-amber-800' : ''}`}>
      <h2 className="text-sm font-semibold flex items-center gap-2">
        {sectionTitle(t, section)}
        {section.internal && <InternalBadge />}
      </h2>
      <div className={`grid gap-4 ${fields.length > 1 ? 'sm:grid-cols-2' : ''}`}>
        {fields.map((f) => (
          <div key={f.key} className={f.wide ? 'sm:col-span-2' : ''}>
            <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium mb-1">{fieldLabel(t, f)}</p>
            <SheetFieldValue t={t} field={f} value={getPath(pkg, f.path)} />
            {f.help && <p className="mt-1 text-[11px] text-muted-foreground">{fieldHelp(t, f)}</p>}
          </div>
        ))}
      </div>
    </Card>
  );
}

/** Variantes lado a lado: una columna por variante, una fila por dato que cambia. */
function VariantsTable({ t, variants }: { t: T; variants: PackageVariant[] }) {
  type Row = { key: string; get: (v: PackageVariant) => string | string[] | undefined };
  const allRows: Row[] = [
    { key: 'videos', get: (v) => (v.videos ? String(v.videos) : undefined) },
    { key: 'min_creators', get: (v) => (v.minCreators ? `${v.minCreators}+` : undefined) },
    { key: 'price', get: (v) => (v.pricing ? `${v.currency ? `${v.currency} ` : ''}${v.pricing}` : undefined) },
    { key: 'production_fee', get: (v) => v.productionFee },
    { key: 'media_commitment', get: (v) => v.mediaCommitment },
    { key: 'deliverables', get: (v) => (v.deliverables?.length ? v.deliverables : undefined) },
    { key: 'notes', get: (v) => v.notes },
  ];
  const rows = allRows.filter((r) => variants.some((v) => r.get(v) != null));
  if (!rows.length) return <p className="text-sm text-muted-foreground">{t('partnerships.package_detail.no_variant_data')}</p>;
  return (
    <div className="overflow-x-auto -mx-1">
      <table className="w-full text-sm">
        <thead>
          <tr>
            <th className="text-left text-[11px] font-medium uppercase tracking-wide text-muted-foreground px-1 pb-2 w-36" />
            {variants.map((v, i) => (
              <th key={v.variantId || i} className="text-left px-2 pb-2 align-bottom">
                <span className={`inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${tierBadgeClass(v.tier)}`}>
                  {variantLabel(v, t('partnerships.packagecard.single_variant'))}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border/40">
          {rows.map((r) => (
            <tr key={r.key} className="align-top">
              <td className="px-1 py-2 text-xs text-muted-foreground">{t(`partnerships.package_detail.variant_${r.key}`)}</td>
              {variants.map((v, i) => {
                const val = r.get(v);
                return (
                  <td key={v.variantId || i} className="px-2 py-2">
                    {Array.isArray(val) ? (
                      <ul className="space-y-1">{val.map((d, j) => <li key={j} className="flex items-start gap-1.5 text-muted-foreground"><span className="h-1.5 w-1.5 rounded-full bg-indigo-400 mt-1.5 shrink-0" />{d}</li>)}</ul>
                    ) : (
                      <span className={val ? (r.key === 'price' ? 'font-semibold' : '') : 'text-muted-foreground/50'}>{val ?? '—'}</span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function PackageDetailPage() {
  const { t, i18n } = useTranslation();
  const { partner: slug, id: packageId } = useParams<{ partner: string; id: string }>();
  const { data: pkg, isLoading, isError, refetch } = usePackage(packageId || '');
  const { data: partners = [] } = usePartners();
  const { data: access } = usePartnershipsAccess();
  const canEdit = canEditSection(access?.editScopes, 'all');
  const [editing, setEditing] = useState(false);
  const config = PARTNER_CONFIG[slug as PartnerSlug];

  if (isLoading) return <div className="flex items-center justify-center py-24"><WinclapLoader /></div>;
  // Error ≠ no existe: con un fallo de red o de permisos se ofrece reintentar en vez de "no encontrado".
  if (isError) {
    return (
      <div className="py-16 text-center text-muted-foreground space-y-3">
        <p>{t('partnerships.package_detail.load_error')}</p>
        <Button variant="outline" size="sm" className="gap-1.5" onClick={() => { void refetch(); }}><RefreshCw className="h-3.5 w-3.5" /> {t('partnerships.package_detail.retry')}</Button>
      </div>
    );
  }
  if (!pkg) return <div className="py-16 text-center text-muted-foreground">{t('partnerships.package_detail.not_found')}</div>;
  // El "volver" va al partner DEL PACK, no al de la URL (un slug cambiado no debe mandar a otro hub).
  const backSlug = partners.find((p) => p.partnerId === pkg.partnerId)?.slug ?? slug;

  const variants = effectiveVariants(pkg);
  const completeness = sheetCompleteness(pkg);
  const expired = isPackageExpired(pkg);
  const e = pkg.eligibility;
  const eligibilityHas = !!(e?.segments?.length || e?.accountSizes?.length || e?.clientType || e?.notes);
  const links = [...(pkg.driveUrl ? [{ label: 'One-pager', url: pkg.driveUrl }] : []), ...(pkg.links ?? [])];
  const section = (key: string) => PACKAGE_SHEET.find((s) => s.key === key)!;

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          <Link to={`/partnerships/${backSlug}/packages`} className="text-muted-foreground hover:text-foreground transition-colors shrink-0 mt-1" aria-label={t('partnerships.package_detail.back_to_packages')} title={t('partnerships.package_detail.back_to_packages')}>
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <Package className={`h-5 w-5 ${config?.textClass}`} />
              <h1 className="text-2xl font-bold">{pkg.name}</h1>
              {pkg.salesModel && <EnumBadge value={pkg.salesModel} tone="info" label={enumLabel(t, pkg.salesModel)} />}
              {pkg.status === 'DRAFT' && <EnumBadge value="draft" tone="muted" label={t('partnerships.packagecard.draft')} />}
              {expired && <EnumBadge value="expired" tone="danger" label={t('partnerships.package_sheet.expired')} />}
            </div>
            {pkg.description && <p className="text-muted-foreground mt-1">{pkg.description}</p>}
            <p className="mt-2 text-xs text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="inline-flex items-center gap-1"><CalendarClock className="h-3.5 w-3.5" />
                {pkg.updatedBy
                  ? t('partnerships.package_sheet.updated_by', { who: pkg.updatedBy, when: fmtRelative(pkg.updatedAt, i18n.language) })
                  : t('partnerships.package_sheet.updated', { when: fmtRelative(pkg.updatedAt, i18n.language) })}
              </span>
              {pkg.validUntil && <span>{t('partnerships.package_sheet.valid_until', { date: formatDate(pkg.validUntil) })}</span>}
              {canEdit && (
                <span title={completeness.missing.length ? t('partnerships.package_sheet.missing', { list: completeness.missing.map((k) => sectionTitle(t, section(k))).join(', ') }) : undefined}>
                  {t('partnerships.package_sheet.completeness', { filled: completeness.filled, total: completeness.total })}
                </span>
              )}
            </p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {canEdit && (
            <Button variant="outline" className="gap-2" onClick={() => setEditing(true)}>
              <Pencil className="h-4 w-4" /> {t('partnerships.package_detail.edit')}
            </Button>
          )}
          {pkg.driveUrl && (
            <a href={pkg.driveUrl} target="_blank" rel="noopener noreferrer">
              <Button className="gap-2">
                <ExternalLink className="h-4 w-4" />
                {t('partnerships.package_detail.view_onepager')}
              </Button>
            </a>
          )}
        </div>
      </div>

      {/* 1. Contexto */}
      <SheetSection t={t} pkg={pkg} section={section('context')} />

      {/* 2. Entregables y precio por variante */}
      {variants.length > 0 && (
        <Card className="p-5 space-y-3">
          <h2 className="text-sm font-semibold">{sectionTitle(t, section('variants'))}</h2>
          <VariantsTable t={t} variants={variants} />
        </Card>
      )}

      {/* 3–4. SLAs / workflow, modelo de venta */}
      <SheetSection t={t} pkg={pkg} section={section('workflow')} />
      <SheetSection t={t} pkg={pkg} section={section('sales')} />

      {/* 5. Elegibilidad (chips + mercados + audiencia) */}
      {(eligibilityHas || pkg.targetAudience || pkg.availableMarkets.length > 0) && (
        <Card className="p-5 space-y-4">
          <h2 className="text-sm font-semibold flex items-center gap-2"><Users className="h-4 w-4 text-blue-500" /> {sectionTitle(t, section('eligibility'))}</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            {!!e?.segments?.length && (
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium mb-1.5">{t('partnerships.package_form.segments_label')}</p>
                <div className="flex flex-wrap gap-1.5">{e.segments.map((s) => <span key={s} className="text-xs px-2 py-1 rounded-md bg-muted">{s}</span>)}</div>
              </div>
            )}
            {!!e?.accountSizes?.length && (
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium mb-1.5">{t('partnerships.package_form.account_sizes_label')}</p>
                <div className="flex flex-wrap gap-1.5">{e.accountSizes.map((s) => <span key={s} className="text-xs px-2 py-1 rounded-md bg-muted">{s}</span>)}</div>
              </div>
            )}
            {e?.clientType && (
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium mb-1.5">{t('partnerships.package_form.client_type_label')}</p>
                <EnumBadge value={e.clientType} tone="neutral" label={enumLabel(t, e.clientType)} />
              </div>
            )}
            {pkg.availableMarkets.length > 0 && (
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium mb-1.5 flex items-center gap-1"><Globe className="h-3 w-3" /> {t('partnerships.package_detail.available_markets')}</p>
                <div className="flex flex-wrap gap-1.5">{pkg.availableMarkets.map((m) => <span key={m} className="text-xs px-2 py-1 rounded-md bg-muted">{m}</span>)}</div>
              </div>
            )}
            {pkg.targetAudience && (
              <div className="sm:col-span-2">
                <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium mb-1">{t('partnerships.package_detail.target_audience')}</p>
                <p className="text-sm text-muted-foreground">{pkg.targetAudience}</p>
              </div>
            )}
            {e?.notes && (
              <div className="sm:col-span-2">
                <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium mb-1">{fieldLabel(t, section('eligibility').fields[0])}</p>
                <p className="text-sm text-muted-foreground whitespace-pre-line">{e.notes}</p>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* 6–10. Condiciones, economics (INTERNO), vista externa, reglas, restricciones */}
      <SheetSection t={t} pkg={pkg} section={section('conditions')} />
      <SheetSection t={t} pkg={pkg} section={section('economics')} />
      <SheetSection t={t} pkg={pkg} section={section('external')} />
      <SheetSection t={t} pkg={pkg} section={section('partnerRules')} />
      <SheetSection t={t} pkg={pkg} section={section('restrictions')} />

      {/* Destacados y productos incluidos (de siempre) */}
      {pkg.highlights.length > 0 && (
        <Card className="p-5 space-y-3">
          <h2 className="text-sm font-semibold flex items-center gap-2">
            <Star className="h-4 w-4 text-yellow-500" />
            {t('partnerships.package_detail.highlights')}
          </h2>
          <ul className="space-y-2">
            {pkg.highlights.map((h, i) => (
              <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                <Star className="h-3 w-3 mt-0.5 text-yellow-400 shrink-0" />
                {h}
              </li>
            ))}
          </ul>
        </Card>
      )}
      {pkg.includedProducts.length > 0 && (
        <Card className="p-5 space-y-3">
          <h2 className="text-sm font-semibold">{t('partnerships.package_detail.included_products')}</h2>
          <ul className="space-y-1">
            {pkg.includedProducts.map((p, i) => (
              <li key={i} className="text-sm text-muted-foreground flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/50" />
                {p}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* 11. Vigencia */}
      <SheetSection t={t} pkg={pkg} section={section('validity')} />

      {/* 12. Links útiles */}
      {links.length > 0 && (
        <Card className="p-5 space-y-3">
          <h2 className="text-sm font-semibold flex items-center gap-2"><Link2 className="h-4 w-4" /> {sectionTitle(t, section('links'))}</h2>
          <ul className="space-y-1.5">
            {links.map((l, i) => (
              <li key={i}>
                <a href={l.url} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline inline-flex items-center gap-1.5">
                  <ExternalLink className="h-3.5 w-3.5" /> {l.label}
                </a>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {pkg.notes && (
        <Card className="p-5 space-y-2 border-dashed border-amber-300/70 bg-amber-50/40 dark:bg-amber-950/10 dark:border-amber-800">
          <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium flex items-center gap-2">{t('partnerships.package_detail.internal_notes')} <InternalBadge /></p>
          <p className="text-sm text-muted-foreground whitespace-pre-line">{pkg.notes}</p>
        </Card>
      )}

      {canEdit && completeness.missing.length > 0 && (
        <p className="text-xs text-muted-foreground">
          {t('partnerships.package_sheet.missing', { list: completeness.missing.filter((k) => !sectionFilled(pkg, section(k))).map((k) => sectionTitle(t, section(k))).join(', ') })}
        </p>
      )}

      {canEdit && (
        <PackageFormDialog open={editing} onOpenChange={setEditing} partnerId={pkg.partnerId} pkg={pkg} />
      )}
    </div>
  );
}
